import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import * as api from '../services/apiClient';
import { useImpersonation } from '../context/ImpersonationContext';

// Module-level cache for blob names per clause - resets on page reload
const blobNamesCache = new Map<number, string[]>();

interface VariableBlobDropdownProps {
  editor: Editor;
  clauseId?: number;  // Optional - if not provided, only creation mode is available
  isOpen: boolean;
  onClose: () => void;
}

export const VariableBlobDropdown: React.FC<VariableBlobDropdownProps> = ({
  editor,
  clauseId,
  isOpen,
  onClose,
}) => {
  const { activeAlias } = useImpersonation();
  const [searchQuery, setSearchQuery] = useState('');
  const [blobNames, setBlobNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Clear cache when impersonation changes
  useEffect(() => {
    blobNamesCache.clear();
    setBlobNames([]);
  }, [activeAlias]);

  // Fetch blob names when dropdown opens
  useEffect(() => {
    if (isOpen && clauseId) {
      fetchBlobNames();
    }
    if (isOpen) {
      setSearchQuery('');
    }
  }, [isOpen, clauseId]);

  // Focus search input when opened
  useEffect(() => {
    if (!isOpen) return;
    const timer: number = window.setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      // Delay to avoid immediate close on button click
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const fetchBlobNames = async () => {
    if (!clauseId) {
      // No clauseId means we can't fetch existing blobs - just allow creation
      setBlobNames([]);
      return;
    }

    // Check cache first
    const cached = blobNamesCache.get(clauseId);
    if (cached) {
      setBlobNames(cached);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getClauseBlobs(clauseId);
      if (response.success && response.data) {
        // Defensive check for blob_names array
        const names = Array.isArray(response.data.blob_names) ? response.data.blob_names : [];
        // Store in cache
        blobNamesCache.set(clauseId, names);
        setBlobNames(names);
      } else {
        setError(response.error || 'Erreur lors du chargement');
      }
    } catch {
      setError('Erreur réseau');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredBlobs = searchQuery.trim()
    ? blobNames.filter(name =>
      name.toLowerCase().startsWith(searchQuery.toLowerCase())
    )
    : blobNames;

  const handleSelectBlob = useCallback((name: string) => {
    editor.chain().focus().insertVariableBlob(name).run();
    setSearchQuery('');
    onClose();
  }, [editor, onClose]);

  const handleCreateNew = useCallback(() => {
    if (searchQuery.trim()) {
      handleSelectBlob(searchQuery.trim());
    }
  }, [searchQuery, handleSelectBlob]);

  // Show create option when there's a search query and no exact match exists
  const hasExactMatch = blobNames.some(name =>
    name.toLowerCase() === searchQuery.toLowerCase()
  );
  const showCreateOption = searchQuery.trim() && !hasExactMatch;

  if (!isOpen) return null;

  return (
    <div ref={dropdownRef} className="variable-blob-dropdown">
      <div className="variable-blob-dropdown-header">
        <svg
          className="variable-blob-dropdown-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={searchInputRef}
          type="text"
          className="variable-blob-dropdown-input"
          placeholder="Rechercher ou créer..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (showCreateOption) {
                handleCreateNew();
              } else if (filteredBlobs.length === 1) {
                handleSelectBlob(filteredBlobs[0]);
              }
            }
          }}
        />
      </div>

      <div className="variable-blob-dropdown-list">
        {isLoading ? (
          <div className="variable-blob-dropdown-loading">Chargement...</div>
        ) : error ? (
          <div className="variable-blob-dropdown-error">{error}</div>
        ) : filteredBlobs.length > 0 ? (
          <>
            <div className="variable-blob-dropdown-section-label">
              Variables existantes
            </div>
            {filteredBlobs.map((name) => (
              <div
                key={name}
                className="variable-blob-dropdown-item"
                onClick={() => handleSelectBlob(name)}
                role="button"
                tabIndex={0}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault();
                    handleSelectBlob(name);
                  }
                }}
              >
                {name}
              </div>
            ))}
          </>
        ) : !showCreateOption ? (
          <div className="variable-blob-dropdown-empty">
            {clauseId ? 'Aucune variable existante' : 'Tapez un nom pour créer une variable'}
          </div>
        ) : null}
      </div>

      {showCreateOption && (
        <div className="variable-blob-dropdown-footer">
          <div
            className="variable-blob-create-hint"
            onClick={handleCreateNew}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                e.preventDefault();
                handleCreateNew();
              }
            }}
          >
            Appuyer sur Entrée pour créer "<strong>{searchQuery.trim()}</strong>"
          </div>
        </div>
      )}
    </div>
  );
};
